import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { User, UserManager, UserManagerSettings } from 'oidc-client';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { distinctUntilChanged, shareReplay, take } from 'rxjs/operators';
import { AppActions } from 'src/app/state/app.actions';
import { selectCurrentProjectId } from 'src/app/state/app.selectors';
import { environment } from 'src/environments/environment';

export function isProdMode(): boolean {
  if (environment.production) {
    return true;
  }
  return false;
}

/** localStorage prefix oidc-client uses for signin-state blobs (WebStorageStateStore). */
const SIGNIN_STATE_PREFIX = 'oidc.';
/** Our own ledger of recently attempted signin states. Deliberately NOT `oidc.*` prefixed — clearStaleState() sweeps that namespace. */
const SIGNIN_LEDGER_KEY = 'ri.signin_state_ledger';
const SIGNIN_LEDGER_MAX_ENTRIES = 5;
const SIGNIN_LEDGER_MAX_AGE_MS = 24 * 60 * 60_000;

interface SigninLedgerEntry {
  /** The OAuth `state` id this attempt used. */
  id: string;
  /** When the attempt started (epoch ms). */
  at: number;
  /** Whether signinRedirectCallback resolved successfully for this state. */
  done: boolean;
}

/**
 * Why a signin callback could or could not find its state, captured before
 * oidc-client consumes (and deletes) it.
 *
 * `processSigninResponse` reads the state with removeState=true — the entry is gone
 * before the token exchange runs — so once the flow has failed there is no way to
 * tell a replayed callback URL from a state that was never there. This snapshot is
 * the only place that distinction survives.
 */
export interface SigninCallbackDiagnostics {
  /** First 8 chars of the `state` param — enough to correlate, not the whole value. */
  stateId: string | null;
  /** Whether the matching `oidc.<state>` entry still existed when the callback started. */
  hadStoredState: boolean;
  /** Whether this browser has seen this state id before. */
  previousAttempt: 'none' | 'started' | 'completed';
  /** Age of that previous attempt, when there was one. */
  msSincePreviousAttempt: number | null;
  /** How many `oidc.*` signin states are sitting in localStorage. */
  pendingStateCount: number;
  /**
   * How this document was loaded: `navigate` (arrived from the IDP redirect, the only
   * healthy value here), `reload` (something reloaded the callback page — a reload
   * mid-exchange destroys the state), `back_forward` (history replay), or `prerender`.
   */
  navigationType: string;
  /**
   * - `ok`            — state was present; any failure came later (network, token endpoint).
   * - `replay`        — this callback URL was already processed in this browser.
   * - `state-missing` — the state was never here: pruned by clearStaleState, storage
   *                     wiped, or the flow started in a different browser/profile.
   */
  verdict: 'ok' | 'replay' | 'state-missing';
  /** One-line rendering of the above, for log messages. */
  summary: string;
}

@Injectable({
  providedIn: 'root',
})
export class OAuthService {
  private _userManager: UserManager;
  private _user: User | null = null;
  private _loginChangedSubject = new ReplaySubject<boolean>(1);
  private _permissionsChangedSubject = new Subject<void>();
  private sub = new ReplaySubject<string>();
  private _renewalInProgress = false;
  // Shared in-flight renewal promise so concurrent callers (token-expiring event,
  // token-expired event, heartbeat) await the same signinSilent() outcome instead of
  // racing. Null when no renewal is running.
  private _renewalPromise: Promise<void> | null = null;
  // True once the first valid user session has been established. Prevents
  // loadSelectableProjects from being dispatched on every silent token renewal
  // (heartbeat / token-expiry refresh), which would cause the home page to
  // re-render every 5 minutes even though nothing meaningful changed.
  private _sessionInitialized = false;
  // BroadcastChannel for same-browser cross-tab logout detection.
  // When any tab calls logout(), it broadcasts 'logout' so other tabs can
  // clear their local state immediately without waiting for token expiry.
  private _logoutChannel = new BroadcastChannel('ri_auth');
  // Snapshot of the signin-state situation taken at the start of the current callback,
  // before oidc-client consumes the state. Read by SigninRedirectCallbackComponent to
  // classify a failure instead of guessing at it.
  private _lastSigninDiagnostics: SigninCallbackDiagnostics | null = null;
  /** Untruncated state id of the callback currently being processed. */
  private _pendingSigninStateId: string | null = null;
  /**
   * The startup renewal of this tab's stored session (refresh token). AuthGuard waits for
   * it before redirecting, so a reload with an expired access token renews instead of
   * bouncing through the AuthServer.
   */
  private _startupRenewal: Promise<void> = Promise.resolve();
  /** In-flight signinRedirect, so concurrent login() callers start exactly one redirect. */
  private _loginRedirect: Promise<void> | null = null;
  public userId$ = this.sub.asObservable();
  public loginChanged = this._loginChangedSubject.asObservable().pipe(
    distinctUntilChanged(),
    shareReplay(1));
  /** Emits whenever permissions are refreshed from the server (userinfo endpoint). */
  public permissionsChanged$ = this._permissionsChangedSubject.asObservable();

  private readonly permissionAliases: Record<string, string[]> = {};

  constructor(private store:Store) {

    this._userManager = new UserManager(this.idpSettings);
    // Purge signin-state blobs (localStorage `oidc.*` keys) left behind by abandoned
    // login flows — nothing else ever removes them. Skipped on the callback route:
    // a user who spent longer than staleStateAge (15 min) at the IS4 login screen
    // would otherwise get their in-flight state pruned here in a race with
    // signinRedirectCallback reading it.
    if (!window.location.pathname.includes('/signin-callback')) {
      this._userManager.clearStaleState().catch(() => {});
    }
    this._userManager.events.addAccessTokenExpired(async () => {
      // The access token expired. Before declaring the session dead — which bounces the
      // user to /session-expired — attempt a silent renewal via the refresh token. When the
      // tab has been backgrounded, browser timer throttling routinely delays the proactive
      // addAccessTokenExpiring renewal until after expiry, so this is the common wake-up path.
      // UpdateUserLogin() coalesces with any renewal already in flight and emits
      // loginChanged(true) via addUserLoaded on success. Only tear the session down if the
      // token is still expired once the renewal attempt has settled.
      try {
        await this.UpdateUserLogin();
      } catch {
        // UpdateUserLogin never rejects — its failure paths resolve — but guard defensively.
      }
      const user = await this._userManager.getUser();
      if (!user || user.expired) {
        this._sessionInitialized = false;
        this._loginChangedSubject.next(false);
      }
    });
    // Renew via refresh token before the access token expires (fires ~60s before expiry).
    // Replaces automaticSilentRenew which always used an iframe regardless of refresh token.
    this._userManager.events.addAccessTokenExpiring(() => {
      this.UpdateUserLogin();
    });
    // Only renew a session this tab already has (the user store is per-tab sessionStorage).
    // A fresh tab has no refresh token, so signinSilent() would fall back to a hidden
    // prompt=none iframe on /connect/authorize — racing AuthGuard's full-page redirect to
    // the same endpoint. The AuthServer 500'd one of the pair on ~1–3 % of sign-ins and
    // the user randomly got its error page (2026-10-02). The redirect alone also keeps the
    // AuthServer's interactive checks (profile confirmation) on every new-tab sign-in.
    this._startupRenewal = this._userManager.getUser().then((user) => {
      if (user) {
        return this.UpdateUserLogin();
      }
      // The callback page has no stored user yet either; finishLogin() reports its outcome.
      if (!window.location.pathname.includes('/signin-callback')) {
        this._loginChangedSubject.next(false);
      }
      return undefined;
    });
    // A page restored from the back/forward cache must be able to start a new login.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        this._loginRedirect = null;
      }
    });
    this._userManager.events.addUserLoaded(async (user) => {
      await this.fetchAndPatchPermissions(user);

      // Set _user with the patched profile so listPermissions() returns fresh values
      this._user = user;
      this._loginChangedSubject.next(this.checkUser(user));
      this._permissionsChangedSubject.next();
      // Dispatch user to store - critical for page reload
      this.store.dispatch(AppActions.setCurrentUser({currentUser: user}));
      // Only load projects and notify other tabs on the first login / post-logout
      // re-login. Silent token renewals (heartbeat, expiry) also fire addUserLoaded
      // but must NOT reload projects — that causes selectCurrentProject to emit,
      // triggering the home page combineLatest and unnecessary API calls every 5 min.
      if (!this._sessionInitialized) {
        this._sessionInitialized = true;
        this.store.dispatch(AppActions.loadSelectableProjects());
        // Notify other tabs in the same browser that a login has occurred so they
        // can revive their session instead of sitting on the session-expired page.
        this._logoutChannel.postMessage('login');
      }
    });
    this.startSessionHeartbeat();
    // Listen for logout broadcast from other tabs in the same browser.
    // Clears local session immediately so the user doesn't stay logged in
    // until the next heartbeat tick.
    this._logoutChannel.onmessage = async (event) => {
      if (event.data === 'logout') {
        console.warn('[OAuthService] Logout broadcast received from another tab');
        this._user = null;
        this._sessionInitialized = false;
        await this._userManager.removeUser();
        this._loginChangedSubject.next(false);
      } else if (event.data === 'login') {
        // Only a tab without a live session needs reviving. Every tab that signs in
        // broadcasts this — opening one more tab must not reload projects and reset the
        // selected project in the tabs that are already working.
        if (this._user && !this._user.expired) {
          return;
        }
        console.log('[OAuthService] Login broadcast received from another tab — reviving session');
        const user = await this._userManager.getUser();
        if (user && !user.expired) {
          await this.fetchAndPatchPermissions(user);
          this._user = user;
          this._loginChangedSubject.next(true);
          this._permissionsChangedSubject.next();
          this.store.dispatch(AppActions.loadSelectableProjects());
          this.store.dispatch(AppActions.setCurrentProject({ currentProjectId: +user.profile['DefaultProjectId'] }));
        }
      }
    };
  }

  /**
   * Fetches the current permissions from the /connect/userinfo endpoint and
   * patches them onto the user profile. The userinfo endpoint returns live
   * server-side permissions (as `currentpermissions` or `Permission`), so
   * admin changes take effect without a full re-login.
   */
  private async fetchAndPatchPermissions(user: User): Promise<void> {
    try {
      const res = await fetch(`${environment.stsAuthority}/connect/userinfo`, {
        headers: { Authorization: `Bearer ${user.access_token}` }
      });
      const info = await res.json();

      // Merge token and userinfo permissions so a partial userinfo payload
      // cannot accidentally hide UI controls for valid JWT permissions.
      const tokenPermissions = this.normalizePermissions((user.profile as any)['Permission']);
      const freshPermissions = this.normalizePermissions(
        info['currentpermissions'] ?? info['currentPermissions'] ?? info['Permission'] ?? info['permission']
      );

      const mergedPermissions = Array.from(new Set([...tokenPermissions, ...freshPermissions]));
      if (mergedPermissions.length > 0) {
        (user.profile as any)['Permission'] = mergedPermissions;
      }

      // Update DefaultProjectId (only on first load — don't overwrite user's selection)
      this.store.select(selectCurrentProjectId).pipe(take(1)).subscribe(currentId => {
        if (!currentId) {
          const defaultProjectId = +(info['DefaultProjectId'] ?? user.profile['DefaultProjectId']);
          this.store.dispatch(AppActions.setCurrentProject({ currentProjectId: defaultProjectId }));
        }
      });
    } catch {
      // Userinfo unreachable (offline / IS4 down) — fall back to JWT claims
      this.store.select(selectCurrentProjectId).pipe(take(1)).subscribe(currentId => {
        if (!currentId) {
          this.store.dispatch(AppActions.setCurrentProject({ currentProjectId: +user.profile['DefaultProjectId'] }));
        }
      });
    }
  }

  private get idpSettings(): UserManagerSettings {
    return {
      authority: environment.stsAuthority,
      client_id: environment.clientId,
      redirect_uri: `${window.location.origin}/signin-callback`,
      // offline_access requests a refresh token from IS4 (requires AllowOfflineAccess = true).
      // oidc-client will renew sessions via a direct backchannel POST to the token endpoint
      // (grant_type=refresh_token) — no iframe needed when a refresh token is present.
      // silent_redirect_uri is kept as fallback for sessions that pre-date offline_access support.
      scope: 'openid profile raksainfo-v4-rest offline_access',
      response_type: 'code',
      post_logout_redirect_uri: `${window.location.origin}/signout-callback`,
      // automaticSilentRenew disabled: we drive renewal manually via addAccessTokenExpiring
      // so that signinSilent() uses grant_type=refresh_token (backchannel) when a refresh
      // token is present. Without this flag oidc-client v1 would also run its own iframe
      // loop on top of our handler.
      automaticSilentRenew: false,
      // silent_redirect_uri is kept as a last-resort fallback: if signinSilent() has no
      // refresh token it falls back to an iframe rather than navigating the main frame
      // (which would cause an infinite reload loop).
      silent_redirect_uri: `${window.location.origin}/assets/silent-callback.html`,
      // Disable the SessionMonitor: it polls checksession_iframe at the IDP and when it
      // detects a change it calls signinSilent() internally via iframe, bypassing our
      // refresh token renewal path entirely. We handle session state via addAccessTokenExpiring
      // and addAccessTokenExpired instead.
      monitorSession: false,
      // Signin state lives in localStorage, which every tab of this origin shares, and
      // clearStaleState() sweeps it globally. At the 15-minute default, a second tab
      // booting the app would delete the in-flight state of a user still sitting at the
      // IS4 login screen (MFA, password reset, or just walking away) — they then return
      // to "No matching state found in storage". An hour covers a slow login while still
      // bounding how long abandoned states accumulate.
      staleStateAge: 60 * 60,
    };
  }
  // Per-tab (sessionStorage survives the round trip to the AuthServer in the same tab).
  // It used to be localStorage, so two tabs signing in at once overwrote each other's
  // target and landed on the wrong page.
  get redirectUrl() {
    return sessionStorage.getItem('redirectUrl') || '';
  }
  set redirectUrl(value: string) {
    sessionStorage.setItem('redirectUrl', value);
  }
  public clearRedirectUrl() {
    sessionStorage.removeItem('redirectUrl');
  }
  public login(): Promise<void> {
    // Coalesce: two guards (or a guard and a click) calling this in the same moment would
    // otherwise each fetch discovery and navigate to /connect/authorize.
    if (!this._loginRedirect) {
      this._loginRedirect = this._userManager
        .signinRedirect()
        .catch((error: HttpErrorResponse) => {
          this._loginRedirect = null;
          console.error('[OAuthService] signinRedirect failed:', error);
        });
    }
    return this._loginRedirect;
  }
  /**
   * Resolves once the startup renewal has settled (succeeded or failed), or after
   * `timeoutMs` — a session without a refresh token renews through a hidden iframe, and a
   * hung one must not hold navigation for oidc-client's 10 s silent timeout.
   */
  public whenStartupSessionChecked(timeoutMs = 4000): Promise<void> {
    return Promise.race([
      this._startupRenewal.catch(() => undefined),
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  }
  /** Diagnostics for the most recent finishLogin() attempt — null before one has run. */
  get lastSigninDiagnostics(): SigninCallbackDiagnostics | null {
    return this._lastSigninDiagnostics;
  }

  public finishLogin = (): Promise<User> => {
    // Must run BEFORE signinRedirectCallback: it deletes the state as it reads it.
    this._lastSigninDiagnostics = this.captureSigninCallbackDiagnostics();
    const callbackPromise = this._userManager.signinRedirectCallback().then((user) => {
      this.markSigninAttemptComplete();
      this._user = user;
      // console.log(user);
      this.store.dispatch(AppActions.setCurrentUser({currentUser:user}));
      this._loginChangedSubject.next(this.checkUser(user));
      // this.isAuthenticated();
      this.sub.next(user.profile.sub);
      return user;
    });
    const timeoutPromise = new Promise<User>((_, reject) =>
      setTimeout(() => reject(new Error('Login timeout: token endpoint did not respond within 30s')), 30_000)
    );
    return Promise.race([callbackPromise, timeoutPromise]);
  };
  public logout = () => {
    // Best-effort refresh token revocation — fire and forget with a 2s timeout.
    // Does NOT block logout. Cross-tab logout also requires IS4 backend config:
    //   options.Authentication.RevokeRefreshTokensOnSignout = true;
    // Without that, IS4 keeps refresh tokens alive after end_session regardless.
    const refreshToken = this._user?.refresh_token as string | undefined;
    if (refreshToken) {
      const controller = new AbortController();
      setTimeout(() => controller.abort(), 2000);
      fetch(`${environment.stsAuthority}/connect/revocation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          token: refreshToken,
          token_type_hint: 'refresh_token',
          client_id: environment.clientId,
        }),
        signal: controller.signal,
      }).catch(() => {});
    }
    // Notify other same-browser tabs to clear their session immediately.
    // Cross-device logout still requires RevokeRefreshTokensOnSignout = true in IS4.
    this._logoutChannel.postMessage('logout');
    this._userManager.signoutRedirect();
  };
  public finishLogout = () => {
    this._user = null;
    return this._userManager.signoutRedirectCallback();
  };
  public isAuthenticated = (): Promise<boolean> => {
    return this._userManager.getUser().then((user) => {
      // Only update _user from storage if we don't already have a valid
      // (potentially patched) instance — avoids overwriting fresh permissions
      // fetched by addUserLoaded / fetchAndPatchPermissions.
      if (!this._user || this._user.expired) {
        if (this._user !== user) {
          this._loginChangedSubject.next(this.checkUser(user));
        }
        this._user = user;
      }
      return this.checkUser(this._user);
    });
  };

  /**
   * Snapshot the signin-state situation for the callback URL currently loaded, and
   * record this attempt in the ledger so a later replay of the same URL can be
   * recognised as such.
   *
   * Best-effort throughout: storage can be unavailable (private mode, blocked cookies)
   * and diagnostics must never be the reason a login fails.
   */
  private captureSigninCallbackDiagnostics(): SigninCallbackDiagnostics {
    const diagnostics: SigninCallbackDiagnostics = {
      stateId: null,
      hadStoredState: false,
      previousAttempt: 'none',
      msSincePreviousAttempt: null,
      pendingStateCount: 0,
      navigationType: 'unknown',
      verdict: 'ok',
      summary: '',
    };

    try {
      const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      diagnostics.navigationType = navigation?.type ?? 'unknown';
    } catch {
      // Navigation Timing unavailable — leave 'unknown'.
    }

    try {
      const stateId = new URLSearchParams(window.location.search).get('state');
      this._pendingSigninStateId = stateId;
      diagnostics.stateId = stateId ? stateId.slice(0, 8) : null;
      diagnostics.pendingStateCount = this.countPendingSigninStates();

      if (stateId) {
        diagnostics.hadStoredState = localStorage.getItem(SIGNIN_STATE_PREFIX + stateId) !== null;

        const previous = this.readSigninLedger().find(entry => entry.id === stateId);
        if (previous) {
          diagnostics.previousAttempt = previous.done ? 'completed' : 'started';
          diagnostics.msSincePreviousAttempt = Date.now() - previous.at;
        }
        this.recordSigninAttempt(stateId);
      }
    } catch {
      // Storage unreadable — leave the defaults, the verdict below stays honest.
    }

    diagnostics.verdict = diagnostics.hadStoredState
      ? 'ok'
      : diagnostics.previousAttempt === 'none'
        ? 'state-missing'
        : 'replay';

    const parts = [
      `verdict=${diagnostics.verdict}`,
      `state=${diagnostics.stateId ?? 'absent'}`,
      `previousAttempt=${diagnostics.previousAttempt}`,
    ];
    if (diagnostics.msSincePreviousAttempt !== null) {
      parts.push(`previousAttemptAge=${Math.round(diagnostics.msSincePreviousAttempt / 1000)}s`);
    }
    parts.push(`pendingStates=${diagnostics.pendingStateCount}`);
    parts.push(`nav=${diagnostics.navigationType}`);
    diagnostics.summary = parts.join(' ');

    return diagnostics;
  }

  /** Marks the in-flight callback's state as having completed its token exchange. */
  private markSigninAttemptComplete(): void {
    const stateId = this._pendingSigninStateId;
    if (!stateId) return;
    try {
      const ledger = this.readSigninLedger();
      const entry = ledger.find(item => item.id === stateId);
      if (entry) {
        entry.done = true;
        this.writeSigninLedger(ledger);
      }
    } catch {
      // Ledger is diagnostics-only — never let it break a successful login.
    }
  }

  private countPendingSigninStates(): number {
    let count = 0;
    for (let index = 0; index < localStorage.length; index++) {
      if (localStorage.key(index)?.startsWith(SIGNIN_STATE_PREFIX)) {
        count++;
      }
    }
    return count;
  }

  private readSigninLedger(): SigninLedgerEntry[] {
    const raw = localStorage.getItem(SIGNIN_LEDGER_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - SIGNIN_LEDGER_MAX_AGE_MS;
    return parsed.filter(
      (entry: any): entry is SigninLedgerEntry =>
        !!entry && typeof entry.id === 'string' && typeof entry.at === 'number' && entry.at > cutoff
    );
  }

  private writeSigninLedger(entries: SigninLedgerEntry[]): void {
    localStorage.setItem(SIGNIN_LEDGER_KEY, JSON.stringify(entries.slice(0, SIGNIN_LEDGER_MAX_ENTRIES)));
  }

  /**
   * Records (or refreshes) an attempt on `stateId`, newest first. An existing entry
   * keeps its `done` flag so a third load still reports that the original exchange
   * had succeeded.
   */
  private recordSigninAttempt(stateId: string): void {
    const ledger = this.readSigninLedger();
    const existing = ledger.find(entry => entry.id === stateId);
    const rest = ledger.filter(entry => entry.id !== stateId);
    this.writeSigninLedger([{ id: stateId, at: Date.now(), done: existing?.done ?? false }, ...rest]);
  }

  private checkUser = (user: User | null): boolean => {
    return !!user && !user.expired;
  };
  getUser() {
    return this._userManager.getUser();
  }

  getUserId() {
    return this._userManager.getUser().then((user) => {
      if (!!user && !user.expired) {
        return user.profile.sub;
      } else {
        return '';
      }
    });
  }
  getCurrentProjectId(): Promise<number> {
    return this._userManager.getUser().then((user) => {
      if (!!user && !user.expired) {

        return +user.profile.DefaultProjectId;
      } else {
        console.log('No user');
        return 0;
      }
    });
  }
  getProjectName(): Promise<string> {
    return this._userManager.getUser().then((user) => {
      if (!!user && !user.expired) {
        return user.profile.DefaultProjectName;
      } else {
        return '';
      }
    });
  }
  getAccessToken(): Promise<string | null> {
    return this._userManager.getUser().then((user) => {
      if (!!user && !user.expired) {
        return user.access_token;
      } else {
        return null;
      }
    });
  }

  /**
   * Like getAccessToken(), but renews first when the stored token has expired or expires
   * within `minValiditySeconds`, or always when `forceRenewal` is set (the server rejected
   * a token that looked valid). For consumers that open their own connections outside
   * HttpClient, such as the SignalR hub: a tab resumed from sleep still holds an expired
   * token until a renewal runs, and getAccessToken() would hand out nothing at all.
   * Resolves null when there is no session or the renewal could not restore one.
   */
  async getValidAccessToken(options: { minValiditySeconds?: number; forceRenewal?: boolean } = {}): Promise<string | null> {
    const minValiditySeconds = options.minValiditySeconds ?? 30;
    const user = await this._userManager.getUser();
    if (!user) {
      return null;
    }
    const secondsLeft = user.expires_in ?? Number.POSITIVE_INFINITY;
    if (!options.forceRenewal && !user.expired && secondsLeft > minValiditySeconds) {
      return user.access_token;
    }
    // Coalesces with any renewal already in flight; on failure it falls back to the
    // stored session and emits loginChanged(false) when that is gone too.
    await this.UpdateUserLogin();
    const renewed = await this._userManager.getUser();
    return renewed && !renewed.expired ? renewed.access_token : null;
  }

  /**
   * False only when the signed-in user's token carries no `role` claim, i.e. an account that has not been
   * given any access yet (permissions come only from roles, the same rule as the API's NO_ROLES filter).
   * No user at all is treated as "has access": that is the login flow's business, not this check's.
   */
  async hasAnyAccess(): Promise<boolean> {
    const user = await this._userManager.getUser();
    if (!user) {
      return true;
    }
    const roles = this.normalizePermissions(user.profile?.['role']).filter(r => !!r);
    return roles.length > 0;
  }

  /**
   * Forces a token renewal (so a role granted since login reaches the token) and reports whether the user
   * now has any access. A failed renewal resolves with the claims the current token carries.
   */
  async renewAndCheckAccess(): Promise<boolean> {
    await this.UpdateUserLogin();
    return this.hasAnyAccess();
  }

  listPermissions(): Promise<string[]> {
    if (!!this._user && !this._user.expired) {
      return Promise.resolve(this.normalizePermissions(this._user.profile.Permission));
    } else {
      // If _user is not available or has expired, fetch it
      return this._userManager.getUser().then((user) => {
        if (!!user && !user.expired) {
          this._user = user; // Cache the user
          return this.normalizePermissions(user.profile.Permission);
        } else {
          return [];
        }
      });
    }
  }

  /**
   * Normalizes the Permission JWT claim to always be a string array.
   * - undefined/null  → []          (user has no permissions at all)
   * - string          → [string]    (OIDC returns a single value as a plain string, not array)
   * - string[]        → string[]    (normal multi-permission case)
   */
  private normalizePermissions(raw: unknown): string[] {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw as string[];
    return [raw as string];
  }

  hasPermission(permissionNames: string | string[]): Promise<boolean> {
    // Ensure permissionNames is an array
    const permissionsArray = Array.isArray(permissionNames) ? permissionNames : permissionNames.split(',').map(p => p.trim());

    return this.listPermissions().then((userPermissions) => {
      const expandedUserPermissions = new Set(
        userPermissions.flatMap((permission) => this.expandPermissionAliases(permission))
      );

      // Check if user permissions include at least one required permission or its aliases.
      return permissionsArray.some(permission =>
        this.expandPermissionAliases(permission).some(required => expandedUserPermissions.has(required))
      );
    });
  }

  private expandPermissionAliases(permission: string): string[] {
    const aliases = this.permissionAliases[permission] ?? [];
    return [permission, ...aliases];
  }

  hasRole(roleName: string): Promise<boolean> {
    return this._userManager.getUser().then((user) => {
      var roles = <string[]>this._user?.profile.role;
      // console.log("User roles", roles, typeof roles);
       // Ensure roles is always an array
    if (!Array.isArray(roles)) {
      roles = [roles];
    }
      if (roles) {
        return roles
          .map((role) => {
            return role.toLocaleLowerCase();
          })
          .includes(roleName.toLocaleLowerCase());
      } else return false;
    });
  }

  UpdateUserLogin(): Promise<void> {
    if (window.location.pathname.includes('/signin-callback')) {
      return Promise.resolve();
    }
    // Coalesce concurrent callers (addAccessTokenExpiring, addAccessTokenExpired, heartbeat,
    // constructor) onto a single in-flight renewal so they all await the same outcome.
    // addAccessTokenExpired relies on this: it must know whether the session actually
    // survived, so returning Promise.resolve() early (as the old guard did) would make it
    // tear the session down while a renewal was still running.
    if (this._renewalInProgress && this._renewalPromise) {
      return this._renewalPromise;
    }
    this._renewalInProgress = true;
    this._renewalPromise = this.performRenewal().finally(() => {
      this._renewalInProgress = false;
      this._renewalPromise = null;
    });
    return this._renewalPromise;
  }

  private performRenewal(): Promise<void> {
    return this._userManager.signinSilent().then((user) => {
      // addUserLoaded handler takes care of fetching fresh permissions, setting _user,
      // firing loginChanged/permissionsChanged, and dispatching setCurrentUser.
      this.sub.next(user.profile.sub);
    }).catch((error) => {
      const errorCode: string = error?.error ?? '';
      const errorMsg: string = typeof error?.message === 'string' ? error.message : '';

      const isInvalidGrant = errorCode === 'invalid_grant' || errorMsg.includes('invalid_grant');
      // login_required / interaction_required are expected when the user simply isn't
      // authenticated yet (e.g. initial app startup, IDP session already ended).
      // Treat them the same as invalid_grant but without the iframe retry.
      const isNotAuthenticated =
        errorCode === 'login_required' ||
        errorCode === 'interaction_required' ||
        errorMsg.includes('login_required') ||
        errorMsg.includes('interaction_required');

      if (isInvalidGrant) {
        // Stale refresh token (session pre-dates offline_access support).
        // Remove it so oidc-client falls back to the iframe silent renew on retry.
        console.warn('[OAuthService] invalid_grant — clearing stale token, retrying via iframe:', error);
        return this._userManager.removeUser()
          .then(() => this._userManager.signinSilent().then((user) => {
            // addUserLoaded handler handles permissions, _user, and events.
            this.sub.next(user.profile.sub);
          }))
          .catch((iframeError) => {
            // iframe also failed (e.g. localhost not registered) — fall back to stored session
            const iframeCode: string = iframeError?.error ?? '';
            const iframeMsg: string = typeof iframeError?.message === 'string' ? iframeError.message : '';
            const iframeExpected =
              iframeCode === 'login_required' || iframeCode === 'interaction_required' ||
              iframeMsg.includes('login_required') || iframeMsg.includes('interaction_required');
            if (!iframeExpected) {
              console.warn('[OAuthService] iframe silent renew also failed, using stored session:', iframeError);
            }
            return this.fallBackToStoredSession();
          });
      }

      if (isNotAuthenticated) {
        // Expected — user is not logged in. Fall back silently to stored session.
        return this.fallBackToStoredSession();
      }

      // Unexpected error (network, timeout, etc.) — log once and fall back to stored session.
      console.warn('[OAuthService] signinSilent failed, falling back to stored session:', error);
      return this.fallBackToStoredSession();
    });
  }

  /**
   * Falls back to the session already in storage. If it is still valid, revives it and
   * emits loginChanged(true); otherwise emits loginChanged(false) so the app routes to
   * /session-expired. Shared by every signinSilent failure branch.
   */
  private async fallBackToStoredSession(): Promise<void> {
    const user = await this._userManager.getUser();
    if (user && !user.expired) {
      await this.fetchAndPatchPermissions(user);
      this._user = user;
      this._loginChangedSubject.next(true);
      this._permissionsChangedSubject.next();
      this.sub.next(user.profile.sub);
      this.store.dispatch(AppActions.setCurrentUser({ currentUser: user }));
    } else {
      this._loginChangedSubject.next(false);
    }
  }

  /**
   * Replaces the checksession iframe: periodically attempts a silent token refresh.
   * If the IDP session was killed remotely (logout from another tab/device, admin
   * revocation), the refresh token grant returns invalid_grant — we then sign the
   * user out locally. This is more reliable than /connect/userinfo which only checks
   * JWT signature/expiry, not whether the IDP session is still alive.
   *
   * Runs every 60 seconds and immediately when the tab regains visibility.
   */
  private startSessionHeartbeat(): void {
    const check = async () => {
      const user = await this._userManager.getUser();
      // No local user or already expired — token expiry events handle this case
      if (!user || user.expired) return;
      // Only needed if the token is not about to expire anyway (expiring event handles that)
      const expiresIn = (user.expires_at ?? 0) - Math.floor(Date.now() / 1000);
      if (expiresIn < 120) return; // addAccessTokenExpiring will fire soon anyway
      // Skip if a renewal is already running (could be from addAccessTokenExpiring or constructor)
      if (this._renewalInProgress) return;

      try {
        await this._userManager.signinSilent();
        // Success — session still valid, addUserLoaded will update state
      } catch (error: any) {
        const isSessionGone =
          error?.error === 'invalid_grant' ||
          error?.error === 'login_required' ||
          (typeof error?.message === 'string' && (
            error.message.includes('invalid_grant') ||
            error.message.includes('login_required')
          ));

        if (isSessionGone) {
          console.warn('[OAuthService] Session heartbeat: IDP session ended remotely, signing out');
          this._user = null;
          await this._userManager.removeUser();
          this._loginChangedSubject.next(false);
        }
        // Any other error (network, timeout) — stay logged in, try again next tick
      }
    };

    // Immediate check when user returns to the tab (catches logout-elsewhere quickly)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) check();
    });

    // Periodic background check — 15 min is sufficient for remote-logout detection.
    // addAccessTokenExpiring already handles proactive renewal before token expiry.
    setInterval(check, 15 * 60_000);
  }

}
